import React, { useCallback, useEffect, useState } from "react";
import { Alert, Spinner } from "reactstrap";
import { Button, Icon } from "../../../components/Component";
import { InputNumber, Modal, Select, Tag } from "antd";
import { normsApis } from "../../../api/Norms";

// Applies a norm to a task as a decoupled material REQUIREMENT: for each active
// norm material it records a computed quantity (= basis qty × consumption norm ×
// (1 + wastage)) and rate on the task, independent of the BOQ. Re-applying the
// same norm replaces its rows. Per-material skips (e.g. computed qty below the
// minimum) come back in `skipped[]` alongside the applied count.

export interface ApplyNormTaskInfo {
  taskId: number;
  taskName?: string;
  workType?: string;
  workQuantity?: number | string | null;
  outputQuantity?: number | string | null;
}

interface SkippedMaterial {
  materialId?: number;
  materialName?: string;
  error?: string;
}

interface ApplyResult {
  applied: number;
  skipped: SkippedMaterial[];
  normCode?: string;
  normTitle?: string;
}

type Stage = "form" | "done";

const ApplyNormModal = ({
  open,
  onClose,
  task,
  onApplied,
  replaceMode = false,
  replaceNormId,
}: {
  open: boolean;
  onClose: () => void;
  task: ApplyNormTaskInfo;
  onApplied?: () => void;
  // Re-apply ("swap on apply"): preselect the current norm; on submit remove it
  // first, then apply the chosen norm. Cancelling leaves the current norm intact.
  replaceMode?: boolean;
  replaceNormId?: number;
}) => {
  const [stage, setStage] = useState<Stage>("form");
  const [normOptions, setNormOptions] = useState<
    { value: number; label: string }[]
  >([]);
  const [loadingNorms, setLoadingNorms] = useState(false);
  const [normId, setNormId] = useState<number | undefined>(undefined);
  const [overrideQty, setOverrideQty] = useState<number | null>(null);
  const [normError, setNormError] = useState("");
  const [qtyError, setQtyError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState<ApplyResult | null>(null);

  const workQuantity = Number(task?.workQuantity) || 0;
  // Open tasks (or ones without a work quantity) have no multiplication basis
  // of their own — a quantity to apply against must be entered.
  const qtyRequired = task?.workType === "Open" || workQuantity <= 0;

  const resetAll = useCallback(() => {
    setStage("form");
    setNormId(undefined);
    setOverrideQty(null);
    setNormError("");
    setQtyError("");
    setSubmitting(false);
    setError("");
    setResult(null);
  }, []);

  const loadNorms = useCallback(async () => {
    try {
      setLoadingNorms(true);
      const res = await normsApis.getNormsDropdown();
      const list: any[] = Array.isArray(res?.data) ? res.data : [];
      if (res?.status === true) {
        setNormOptions(
          list.map((n: any) => ({
            value: n.normId,
            label: n.normTitle ? `${n.normCode} — ${n.normTitle}` : n.normCode,
          })),
        );
      } else {
        setError(res?.message || "Failed to load the norms list");
      }
    } catch {
      setError("Failed to load the norms list");
    } finally {
      setLoadingNorms(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      resetAll();
      loadNorms();
      // Re-apply: preselect the currently applied norm.
      if (replaceMode && replaceNormId) setNormId(replaceNormId);
    }
  }, [open, resetAll, loadNorms, replaceMode, replaceNormId]);

  const validate = () => {
    let ok = true;
    if (!normId) {
      setNormError("Please select a norm");
      ok = false;
    } else {
      setNormError("");
    }
    if (qtyRequired && !(Number(overrideQty) > 0)) {
      setQtyError(
        "This task has no work quantity — enter the quantity to apply the norm against",
      );
      ok = false;
    } else if (overrideQty != null && !(Number(overrideQty) > 0)) {
      setQtyError("Override quantity must be greater than 0");
      ok = false;
    } else {
      setQtyError("");
    }
    return ok;
  };

  const handleApply = async () => {
    if (!validate()) return;
    setError("");
    try {
      setSubmitting(true);
      // Re-apply (swap on apply): remove the current norm first so the new
      // selection replaces it. Done here (not on button click) so cancelling
      // the modal leaves the current norm intact.
      if (replaceMode && replaceNormId) {
        const rm = await normsApis.removeNormFromTask({
          taskId: Number(task.taskId),
          normId: Number(replaceNormId),
          reason: "Re-applied",
        });
        if (rm?.status !== true) {
          setError(
            rm?.data?.message || rm?.message || "Failed to remove the current norm",
          );
          setSubmitting(false);
          return;
        }
      }
      const res = await normsApis.applyNormToTask({
        taskId: Number(task.taskId),
        normId: Number(normId),
        ...(Number(overrideQty) > 0 ? { overrideQty: Number(overrideQty) } : {}),
      });

      // makeApiCall resolves success as { status: true, data } and 4xx as the
      // raw response { status: <number>, data } (see axiosInstance interceptor).
      if (res?.status === true) {
        const data = res?.data || {};
        setResult({
          applied: Number(data.applied) || 0,
          skipped: Array.isArray(data.skipped) ? data.skipped : [],
          normCode: data.normCode,
          normTitle: data.normTitle,
        });
        setStage("done");
        return;
      }

      const rawMessage = res?.data?.message ?? res?.message;
      const message = Array.isArray(rawMessage)
        ? rawMessage.join("; ")
        : rawMessage || "Failed to apply the norm. Please try again.";
      setError(message);
    } catch (err: any) {
      setError(err?.message || "Something went wrong while applying the norm");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDone = () => {
    onApplied?.();
    onClose();
  };

  return (
    <Modal
      title={`${replaceMode ? "Re-apply Norm" : "Apply Norm"}${
        task?.taskName ? ` — ${task.taskName}` : ""
      }`}
      open={open}
      footer={null}
      onCancel={stage === "done" ? handleDone : onClose}
      maskClosable={false}
      width={520}
    >
      {stage === "form" && (
        <div>
          <p className="mb-2">
            Applying a norm records its active materials as a requirement on this
            task:{" "}
            <em>quantity = basis quantity × consumption norm × (1 + wastage)</em>
            , priced from the norm line. A norm can be applied to a task only
            once — to re-apply, remove it (with a reason) first.
          </p>
          <p className="mb-3" style={{ fontSize: 13 }}>
            {qtyRequired ? (
              <Tag color="gold" style={{ whiteSpace: "normal" }}>
                Open task — enter the quantity to apply the norm against below
              </Tag>
            ) : (
              <Tag color="blue">
                Task work quantity: {workQuantity}
                {task?.outputQuantity
                  ? ` (output basis: ${task.outputQuantity})`
                  : ""}
              </Tag>
            )}
          </p>

          <div className="form-group">
            <label className="form-label">
              Norm <span style={{ color: "red" }}>*</span>
            </label>
            <Select
              style={{ width: "100%" }}
              placeholder="Select norm"
              showSearch
              optionFilterProp="label"
              loading={loadingNorms}
              options={normOptions}
              value={normId}
              onChange={(value) => {
                setNormId(value);
                setNormError("");
              }}
              disabled={submitting}
              status={normError ? "error" : undefined}
            />
            {normError && (
              <span className="invalid" style={{ color: "#e85347", fontSize: 12 }}>
                {normError}
              </span>
            )}
          </div>

          <div className="form-group mt-2">
            <label className="form-label">
              Override Quantity{" "}
              {qtyRequired ? (
                <span style={{ color: "red" }}>*</span>
              ) : (
                <span className="text-soft" style={{ fontWeight: 400 }}>
                  (optional — replaces the task work quantity as the basis)
                </span>
              )}
            </label>
            <InputNumber
              style={{ width: "100%" }}
              placeholder={
                qtyRequired
                  ? "Enter quantity to apply the norm against"
                  : `Defaults to task work quantity (${workQuantity})`
              }
              min={0}
              value={overrideQty}
              onChange={(value) => {
                setOverrideQty(value as number | null);
                setQtyError("");
              }}
              disabled={submitting}
              status={qtyError ? "error" : undefined}
            />
            {qtyError && (
              <span className="invalid" style={{ color: "#e85347", fontSize: 12 }}>
                {qtyError}
              </span>
            )}
          </div>

          <Alert
            isOpen={!!error}
            color="danger"
            className="py-1 mt-3 mb-0"
            style={{ textAlign: "left", fontSize: "14px" }}
          >
            {error}
          </Alert>

          <div className="d-flex mt-4" style={{ gap: 12 }}>
            {submitting ? (
              <Button color="primary" type="button">
                <Spinner size="sm" color="light" />
              </Button>
            ) : (
              <Button color="primary" onClick={handleApply}>
                <Icon name="check" />
                <span>Apply Norm</span>
              </Button>
            )}
            {!submitting && (
              <Button
                color="primary"
                className="btn-dim btn btn-secondary"
                onClick={onClose}
              >
                Cancel
              </Button>
            )}
          </div>
        </div>
      )}

      {stage === "done" && result && (
        <div>
          <div className="text-center py-2">
            <Icon name="check-circle" style={{ fontSize: 42, color: "#1ee0ac" }} />
            <h6 className="mt-2">{result.applied} material line(s) applied</h6>
            {(result.normCode || result.normTitle) && (
              <p className="text-soft mb-0">
                {result.normCode}
                {result.normTitle ? ` — ${result.normTitle}` : ""}
              </p>
            )}
          </div>
          {result.skipped.length > 0 && (
            <Alert
              color="warning"
              className="py-2 mt-2 mb-0"
              style={{ textAlign: "left", fontSize: "14px" }}
            >
              <strong>Skipped materials ({result.skipped.length})</strong>
              <ul className="mt-1 mb-0" style={{ paddingLeft: 18 }}>
                {result.skipped.map((skip, index) => (
                  <li key={index}>
                    <strong>
                      {skip.materialName || `Material #${skip.materialId}`}
                    </strong>
                    {skip.error ? ` — ${skip.error}` : ""}
                  </li>
                ))}
              </ul>
            </Alert>
          )}
          <div className="d-flex justify-content-center mt-3">
            <Button color="primary" onClick={handleDone}>
              Done
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default ApplyNormModal;
